import {apiClient} from "../connection/BlinkApiClient";
import {CycleUnit, RecurringPaymentStoreObject} from "./RecurringPaymentStoreObject";
import {field} from "../../stem-core/state/StoreField";
import {cleanObject} from "../../stem-core/base/Utils";
import {getFeeRawPercentage} from "./DonationOfferStore";
import {BaseStore, globalStore} from "../../stem-core/state/Store";


@globalStore
export class Donation extends BaseStore("Donation", {}, RecurringPaymentStoreObject) {
    @field(Boolean) coversFees: boolean; // TODO rename from the backend to donorCoversFees
    @field(CycleUnit) frequency: CycleUnit;

    isRecurring(): boolean {
        return !this.frequency.isOneTime();
    }

    serializeForSDK(): any {
        // TODO @branch check if this works
        // TODO migrate `amount` + `currencyCode` to Money.toRichJSON() once merchants have been notified.
        const {id, blinkSignature, userId, amount, createdAt, canceledAt, frequency, activeUntil} = this;
        let obj: Record<string, any> = {id, blinkSignature, userId, amount, createdAt, canceledAt, frequency, activeUntil};

        obj.currencyCode = this.getCurrency().getIsoCode();

        return obj;
    }

    updatePaymentMethod(paymentMethodId: string): Promise<any> {
        return apiUpdateRecurringDonation(this, {preferredPaymentMethodId: paymentMethodId});
    }

    reactivate(): Promise<any> {
        return apiUpdateRecurringDonation(this, {canceled: false});
    }

    getShortenedFrequency(): string {
        return this.frequency.getAbrev();
    }

    // TODO @branch this should be shared with Subscription Offer
    formatPriceShortened(): string {
        if (this.isRecurring()) {
            return `${this.getPrice()}/${this.getShortenedFrequency()}`;
        }
        return String(this.getPrice());
    }

    formatPrice(): string {
        if (this.isRecurring()) {
            return `${this.getPrice()}/${this.frequency.name.toLowerCase()}`;
        }
        return String(this.getPrice());
    }

    // TODO @cleanup WAT? This does the reverse fee calc rule. The original amount should be stored.
    computeAmount(): number {
        return this.coversFees ? Math.floor((this.amount - 3e5) / (1 + getFeeRawPercentage(this.getMerchant().id)) / 1e6) * 1e6 : this.amount;
    }

    isActive(): boolean {
        return super.isActive() && this.status === RecurringPaymentStoreObject.Status.ACTIVE;
    }

    static getRecurring(): Donation[] {
        return this.all().filter(entry => entry.isRecurring());
    }

    static getPresentRecurring(): Donation[] {
        return [...this.getActiveRecurring(), ...this.getRecurringUnpaid()];
    }

    static getActiveRecurring(): Donation[] {
        return this.getRecurring().filter(donation => donation.isActive());
    }

    static getRecurringUnpaid(): Donation[] {
        return this.getRecurring().filter(donation => donation.isUnpaid());
    }

    static getPastRecurring(): Donation[] {
        return this.getRecurring().filter(donation => !donation.isActive() && !donation.isUnpaid());
    }

    static getOneTime(): Donation[] {
        return this.all().filter(entry => !entry.isRecurring());
    }

    static computeFeeFromAmount(amount: number, merchantId: number): number {
        if (!amount) {
            return 0;
        }
        return Math.ceil((getFeeRawPercentage(merchantId) * amount / 1e6 + 0.3) * 2) * 5e5;
    }
}

export async function apiDonate(donationDetails: any): Promise<Donation> {
    const response = await apiClient.post("/donations/donate", donationDetails);
    return Donation.loadObject(response);
}

export async function apiUpdateRecurringDonation(donation: Donation, fields: any): Promise<any> {
    fields = cleanObject(fields);
    return apiClient.post("/donations/update", {
        donationId: donation.id,
        ...fields,
    })
}


export async function apiUpdateDonationAmount(donation: Donation, amount: number, coversFees: boolean = false): Promise<any> {
    return apiUpdateRecurringDonation(donation, {amount, coversFees});
}

export async function apiCancelDonation(donation: Donation): Promise<any> {
    return apiUpdateRecurringDonation(donation, {canceled: true});
}
